import { z } from 'zod';
import { FORM_FIELD_TYPES } from '../enums.js';
import { EntityMetaSchema, zEnum, zNonEmpty } from './common.js';

/** A single field definition within a form/checklist template. */
export const FormFieldSchema = z.object({
  id: z.string(),
  label: zNonEmpty.max(200),
  type: zEnum(FORM_FIELD_TYPES),
  options: z.array(z.string().max(120)).default([]), // for select
  required: z.boolean().default(false),
  helpText: z.string().max(500).optional(),
});
export type FormField = z.infer<typeof FormFieldSchema>;

/** Tenant-configurable form/checklist template (safety inspection, HVAC startup, completion, …). */
export const FormTemplateSchema = EntityMetaSchema.extend({
  name: zNonEmpty.max(120),
  description: z.string().max(1000).optional(),
  fields: z.array(FormFieldSchema).min(1),
  active: z.boolean().default(true),
  appliesToJobTypes: z.array(z.string().max(80)).default([]), // optional filter
});
export type FormTemplate = z.infer<typeof FormTemplateSchema>;

export const CreateFormTemplateSchema = z.object({
  name: zNonEmpty.max(120),
  description: z.string().max(1000).optional(),
  fields: z.array(FormFieldSchema.omit({ id: true })).min(1, 'Add at least one field'),
  appliesToJobTypes: z.array(z.string().max(80)).optional(),
});
export type CreateFormTemplateInput = z.infer<typeof CreateFormTemplateSchema>;
export const UpdateFormTemplateSchema = CreateFormTemplateSchema.partial().extend({ active: z.boolean().optional() });
export type UpdateFormTemplateInput = z.infer<typeof UpdateFormTemplateSchema>;

/**
 * A completed (or in-progress) instance of a form, attached to a Job. Photo/signature values store
 * S3 keys; pass/fail stores 'pass'|'fail'|'na'. Kept flexible (string map) validated against template.
 */
export const FormInstanceSchema = EntityMetaSchema.extend({
  templateId: z.string(),
  templateName: z.string(),
  jobId: z.string(),
  customerId: z.string(),
  values: z.record(z.string(), z.string()).default({}),
  completed: z.boolean().default(false),
  completedByUserId: z.string().optional(),
  completedAt: z.string().optional(),
});
export type FormInstance = z.infer<typeof FormInstanceSchema>;

export const SubmitFormInstanceSchema = z.object({
  templateId: zNonEmpty,
  jobId: zNonEmpty,
  values: z.record(z.string(), z.string()).default({}),
  completed: z.boolean().optional(),
});
export type SubmitFormInstanceInput = z.infer<typeof SubmitFormInstanceSchema>;

/** Validate submitted values against a template's fields (required + select membership). */
export function validateFormValues(template: FormTemplate, values: Record<string, string>): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const f of template.fields) {
    const v = values[f.id];
    if ((v === undefined || v === '') && f.required) {
      errors[f.id] = `${f.label} is required`;
      continue;
    }
    if (v && f.type === 'select' && f.options.length && !f.options.includes(v)) errors[f.id] = `${f.label}: invalid option`;
    if (v && f.type === 'number' && Number.isNaN(Number(v))) errors[f.id] = `${f.label} must be a number`;
    if (v && f.type === 'passfail' && !['pass', 'fail', 'na'].includes(v)) errors[f.id] = `${f.label}: must be pass/fail/na`;
  }
  return errors;
}
